// MACHINERY V2 (Stroje) — the sheet's row/section/chip atoms (M2.16a).
//
// Spec + rendered truth: mobile/screenshots/02-filter-sheet-open.png. Reference
// impl: mobile/machinery-mobile.jsx MbSheetRow (:116-124) and MbFilterSheet's
// section eyebrows and chips (:139-155).
//
// KIT, NOT FLEET: these are the pieces every sheet in the module is made of, so
// 16c's katalog sheet and 16d's profile sheets compose them instead of drawing a
// second 19px checkbox row. What is fleet-specific — which sections exist, what
// they count — lives in fleet/fleetSheetSections.tsx. The phone nav drawer
// (chrome/MobileDrawer.tsx) composes these too: its sections, its language
// chips and its palette chips are THESE atoms, not a second set.
//
// >>> THE CHECKBOX IS IMPORTED FROM FilterRail, NOT FORKED. <<< FilterRail
// exports `CheckBox` precisely so there is ONE row-checkbox implementation; it
// is already cross-imported by catalog/CatalogFilterRail.tsx (FilterRail.tsx
// :35-40 records that decision). This sheet needs the same control at 19px
// instead of 13px, so the ATOM gained a `size` prop rather than this file
// gaining a second checkbox. The import arrow runs kit -> fleet, which is
// backwards for a kit; it is deliberate and it is the lesser evil — the same
// precedent CatalogFilterRail set, and one implementation of a tick beats a
// tidy dependency graph. If a third surface needs it, move the atom to this
// folder and update the two importers.
//
// >>> 44px HIT TARGETS OVERRIDE THE BOARD, ON BOTH AXES. <<< The law as amended
// at the M2.16a fix pass reads ">= 44px BOTH axes; minWidth accompanies height
// everywhere the law applies". The board draws its sheet chips at h38
// (machinery-mobile.jsx:149) and says nothing about width, so a two-letter chip
// ("SK", "EN") drawn to the board is a ~34x38 target. The rows are already 44
// (MbSheetRow's `minHeight: 44`) and full-width; the chips and the text action
// carry `height: 44` AND `minWidth: 44` here. That is the one place this file
// departs from the drawing on purpose. Do not "fix" it back to 38.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { CheckBox } from "../fleet/FilterRail";

/** Eyebrow + body. Every section after the first carries the board's hairline
 *  divider above it (machinery-mobile.jsx:146).
 *
 *  `note` is a short caption printed beside the eyebrow in the module accent.
 *  It exists for ONE job the boards do not draw: a sheet that mixes apply
 *  models has to say which is which per section, and the eyebrow is the last
 *  thing read before the control is tapped. See fleetSheetSections.tsx. */
export const SheetSection: React.FC<{
  title: string;
  note?: string;
  divider?: boolean;
  children: React.ReactNode;
}> = ({ title, note, divider = true, children }) => (
  <div
    style={
      divider
        ? { borderTop: "1px solid var(--border-soft)", marginTop: 8, paddingTop: 4 }
        : undefined
    }
  >
    <div
      className="eyebrow"
      style={{
        padding: divider ? "8px 0 2px" : "6px 0 2px",
        display: "flex",
        alignItems: "baseline",
        gap: 8,
        minWidth: 0,
      }}
    >
      <span style={{ flexShrink: 0 }}>{title}</span>
      {note && (
        <span
          style={{
            // `.eyebrow` uppercases and wide-tracks; the note deliberately does
            // neither, so it reads as an aside rather than a second heading.
            textTransform: "none",
            letterSpacing: "0.01em",
            color: "var(--mod-accent)",
            fontWeight: 600,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {note}
        </span>
      )}
    </div>
    {children}
  </div>
);

export interface SheetCheckRowProps {
  label: string;
  /** Right-aligned mono count. Omitted where the surface has no honest number
   *  to print — see the fleet sheet's decommissioned-scope branch. */
  count?: number;
  checked: boolean;
  /** Second-level rows (a hall under its plant) indent like the rail's. */
  depth?: number;
  /** Small status mark left of the label, tinted by the caller. */
  icon?: React.ReactNode;
  iconInk?: string;
  onToggle: () => void;
}

export const SheetCheckRow: React.FC<SheetCheckRowProps> = ({
  label,
  count,
  checked,
  depth = 0,
  icon,
  iconInk,
  onToggle,
}) => (
  // A button with aria-pressed, not the board's <label>: it is the same control
  // the rail uses (FilterRail's Row), so keyboard behaviour and the announced
  // state match across the two filter surfaces.
  <button
    type="button"
    onClick={onToggle}
    aria-pressed={checked}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      width: "100%",
      minHeight: 44,
      paddingLeft: depth * 16,
      paddingRight: 0,
      border: 0,
      background: "transparent",
      cursor: "pointer",
      fontFamily: "inherit",
      textAlign: "left",
      // 44, NEVER 0 — the pair to `minHeight: 44` above. Inert today
      // (`width: "100%"` in a block container), kept as a minimum because a `0`
      // beside a stated minimum is the shape that voided TabSelect's and
      // SpecSectionsV2's width half. The label span truncates; this does not.
      minWidth: 44,
    }}
  >
    <CheckBox checked={checked} size={19} />
    {icon && <span style={{ color: iconInk, display: "inline-flex", flexShrink: 0 }}>{icon}</span>}
    <span
      style={{
        fontSize: 13.5,
        color: "var(--ink)",
        flex: 1,
        minWidth: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {label}
    </span>
    {count !== undefined && (
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10.5,
          color: "var(--faint)",
          flexShrink: 0,
        }}
      >
        {count}
      </span>
    )}
  </button>
);

export interface SheetChipProps {
  label: string;
  active: boolean;
  /** Optional trailing count, mono — same treatment FleetToolbar's Chip gives
   *  its scope counts, so the two surfaces print a number the same way. */
  count?: number;
  /** `code` renders a short mono token (a language code, a palette id) instead
   *  of a word. It is a TYPOGRAPHIC variant, not a second chip: the box, the
   *  states and the 44x44 target are shared, which is the whole reason the
   *  drawer's language row is this atom rather than a fork of it. */
  variant?: "text" | "code";
  /** Accessible name when `label` is an abbreviation the code spells out. */
  ariaLabel?: string;
  onClick: () => void;
}

/** Pill option inside a sheet. Wrapping is fine HERE (the board wraps its
 *  group-by chips) — the never-wrap standard is about chip rows on a page. */
export const SheetChip: React.FC<SheetChipProps> = ({
  label,
  active,
  count,
  variant = "text",
  ariaLabel,
  onClick,
}) => {
  const isCode = variant === "code";
  return (
    <button
      type="button"
      className="btn"
      onClick={onClick}
      aria-pressed={active}
      aria-label={ariaLabel}
      style={{
        height: 44,
        // BOTH AXES — see the header. "SK" is 2 characters wide.
        minWidth: 44,
        justifyContent: "center",
        borderRadius: 999,
        fontFamily: isCode ? "var(--font-mono)" : "inherit",
        fontSize: isCode ? 11.5 : 12.5,
        letterSpacing: isCode ? "0.05em" : undefined,
        background: active ? "var(--mod-accent)" : "var(--card)",
        color: active ? "var(--mod-accent-fg)" : "var(--ink-2)",
        borderColor: active ? "var(--mod-accent)" : "var(--border)",
        fontWeight: active ? (isCode ? 700 : 600) : 400,
      }}
    >
      {label}
      {count !== undefined && (
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10.5,
            opacity: active ? 0.9 : 0.7,
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
};

/** Row of chips inside a sheet. */
export const SheetChipRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: "flex", gap: 6, padding: "8px 0 4px", flexWrap: "wrap" }}>
    {children}
  </div>
);

/** Accent text action (the header's "Zrusit vsetko"). 44x44 target minimum,
 *  12.5px text. */
export const SheetTextAction: React.FC<{ label: string; onClick: () => void }> = ({
  label,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      height: 44,
      minWidth: 44,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      border: 0,
      background: "transparent",
      padding: "0 2px",
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 12.5,
      fontWeight: 600,
      color: "var(--mod-accent)",
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}
  >
    {label}
  </button>
);
